
import { Consent, ConsentInitiationResult, ConsentInitiationStatus } from "@/types/openpayments/consent";
import { ConsentApiClient, type InitiateConsentCreationOptions } from "../clients/consentApiClient";

/** Which SCA challenge fields a consent response actually carried — logged identically by both
 *  calls below, and the first thing to check when a flow stalls with no challenge at all. */
const challengeShape = (r: ConsentInitiationResult | ConsentInitiationStatus) => ({
  qrCodeImage: !!r.qrCodeImage,
  qrToken: !!r.qrToken,
  scaRedirect: !!r.scaRedirect,
  bankIdUrl: !!r.bankIdUrl,
  refreshInterval: r.refreshInterval,
});

export class ConsentService {
  
  /**
   * Initiate consent creation for accessing bank accounts
   * @returns Promise with enhanced initiation details
   */
  async initiateConsentCreation(options: InitiateConsentCreationOptions): Promise<ConsentInitiationResult> {
    const edgeResponse = await ConsentApiClient.initiateConsentCreation(options);
    if (edgeResponse) {
      console.log(`Consent creation initiated with ID ${edgeResponse.consentId}, auth ID: ${edgeResponse.authorisationId}, method: ${edgeResponse.authMethod}`, challengeShape(edgeResponse));
      return edgeResponse;
    }
    throw new Error("Failed to initiate consent creation");
  }

  /**
   * Get a specific consent by ID
   * @param consentId The ID of the consent
   * @param bic Bank BIC code (required)
   * @returns Promise<Consent>
   */
  async getConsent(consentId: string, bic: string): Promise<Consent> {
    const edgeResponse = await ConsentApiClient.getConsent(consentId, bic);
    if (edgeResponse) {
      console.log(`Consent retrieved for ID ${edgeResponse.consentId}`);
      return edgeResponse;
    }
    throw new Error("Failed to get consent");
  }

  /**
   * Get consent initiation status and auto-create DB consent when completed
   * @param consentId The ID of the consent
   * @param authorisationId The authorization ID (optional for existing consents)
   * @param bic Bank BIC code (required)
   * @returns Promise with enhanced initiation status
   */
  async getConsentInitiationStatus(consentId: string, authorisationId: string | null, bic: string, ref?: string, countryCode?: string): Promise<ConsentInitiationStatus> {
    const edgeResponse = await ConsentApiClient.getConsentInitiationStatus(consentId, authorisationId, bic, ref, countryCode);
    if (edgeResponse) {
      console.log(`Consent initiation status retrieved for ID ${edgeResponse.consentId}: SCA=${edgeResponse.scaStatus}, completed=${edgeResponse.isCompleted}`, challengeShape(edgeResponse));
      return edgeResponse;
    }
    throw new Error("Failed to get consent initiation status");
  }

  /**
   * Refresh a specific consent by creating a new one for the same account
   * @param consentId The ID of the consent to refresh
   * @returns Promise<Consent>
   */
  async refreshConsent(consentId: string): Promise<Consent> {
    const edgeResponse = await ConsentApiClient.refreshConsent(consentId);
    if (edgeResponse) {
      console.log(`Consent refreshed for ID ${edgeResponse.consentId}`);
      return edgeResponse;
    }
    throw new Error("Failed to refresh consent");
  }
}

export default ConsentService;
